import { useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

/** How far one arrow key moves the edge. */
const KEYBOARD_STEP = 16;

interface PaneResizerProps {
  /** Which edge of its own pane the handle sits on. */
  edge: 'start' | 'end';
  /** Named for screen readers — there are two of these on the page. */
  label: string;
  onResize: (width: number) => void;
  onReset: () => void;
}

/**
 * The drag handle that resizes a side pane.
 *
 * Rendered INSIDE the pane it resizes and positioned over its edge, so it needs
 * no knowledge of the grid: the width it reports is measured off its parent's
 * own box each time, which means a slow drag cannot accumulate drift and a
 * pointer that leaves the window and comes back picks up where it is, not where
 * it was.
 *
 * The bounds live in {@link usePaneWidth} rather than here — the same clamp has
 * to apply to a stored width from a wider window, so it cannot sit in a
 * component that only exists during a drag.
 */
export function PaneResizer({
  edge,
  label,
  onResize,
  onReset,
}: PaneResizerProps) {
  const handleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const paneRect = (): DOMRect | undefined =>
    handleRef.current?.parentElement?.getBoundingClientRect();

  const reportWidthAt = (clientX: number) => {
    const rect = paneRect();
    if (rect === undefined) return;
    // The queue grows as the pointer moves away from its start edge; the rail,
    // which is dragged by its own start edge, grows as it moves toward its end.
    onResize(edge === 'end' ? clientX - rect.left : rect.right - clientX);
  };

  const handlePointerDown = (pointerEvent: PointerEvent<HTMLDivElement>) => {
    // Capture so the drag survives the pointer crossing the reader, an iframe,
    // or the window edge — without it the pane sticks the moment the cursor
    // outruns the 9px handle.
    pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    pointerEvent.preventDefault();
    setIsDragging(true);
  };

  const handlePointerMove = (pointerEvent: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    reportWidthAt(pointerEvent.clientX);
  };

  const endDrag = (pointerEvent: PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    pointerEvent.currentTarget.releasePointerCapture(pointerEvent.pointerId);
    setIsDragging(false);
  };

  const handleKeyDown = (keyboardEvent: KeyboardEvent<HTMLDivElement>) => {
    const rect = paneRect();
    if (rect === undefined) return;

    const growsWithArrowRight = edge === 'end';
    if (keyboardEvent.key === 'ArrowRight') {
      onResize(
        rect.width + (growsWithArrowRight ? KEYBOARD_STEP : -KEYBOARD_STEP)
      );
    } else if (keyboardEvent.key === 'ArrowLeft') {
      onResize(
        rect.width + (growsWithArrowRight ? -KEYBOARD_STEP : KEYBOARD_STEP)
      );
    } else if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
      onReset();
    } else {
      return;
    }
    // Only once a key was one of ours: the rail's own shortcuts still need the
    // rest, and the queue scrolls with the arrows everywhere else.
    keyboardEvent.preventDefault();
  };

  return (
    <div
      ref={handleRef}
      className="gr-resizer"
      data-edge={edge}
      data-dragging={isDragging ? 'true' : undefined}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      tabIndex={0}
      title={`${label} — drag, or double-click to reset`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onReset}
      onKeyDown={handleKeyDown}
    />
  );
}
